import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import { Location } from 'history';
import PrintableReturnInfo, { GRBillState } from './PrintableReturnInfo';
import ReactToPrint from 'react-to-print';
import LoadingButtonBase from './common/LoadingButtonBase';
import { PrintHistoryState } from './PrintableReturnInfo';
import { Button, Alert } from 'reactstrap';
import { History } from 'history';
import { Address } from '../types/GrossReceiptReturnTypes';
import { parseResponse } from '../utility/FetchFunctions';
import { formatAddress } from '../utility/InputFunctions';
import { FetchSuccess, FetchError } from '../types/FetchTypes';
import { mockSuccessResponse } from '../utility/FetchFunctions';

export interface PrintSubmittedReturnProps {
    location: Location<PrintHistoryState>;
    history: History;
    isTest: boolean;
};

interface PrintSubmittedReturnState {
    printing: boolean;
    error: string;
    countyPhoneNumber: string | undefined;
    countyAddress: string | undefined;
    isFetching: boolean;
}

export default class PrintSubmittedReturn extends Component<PrintSubmittedReturnProps, PrintSubmittedReturnState> {
    state: PrintSubmittedReturnState = {
        printing: false,
        error: '',
        countyPhoneNumber: '',
        countyAddress: '',
        isFetching: true
    }
    static displayName = PrintSubmittedReturn.name;
    
    private componentRef: HTMLDivElement | null = null;

    public static defaultProps = {
        isTest: false,
    };

    componentDidMount():void {
        this.getCountyPhoneNumber();
        this.getCountyAddress();
        this.setState({ isFetching:false })
    }
    render() {
        return (
            <div>
                {this.state.isFetching ? 'Loading' :
                    this.props.location.state === undefined || this.props.location.state.accountNum === undefined ?
                    (
                        <Redirect to='/' />
                    ) :
                    this.printableComponent
                }
            </div>
        );
    }

    fetchPaymentURL = async (): Promise<Response> => await fetch('api/configuration/PaymentServiceURL'); 

    getFetchCountyPhoneResponse = async(route: string): Promise<FetchSuccess<string> | FetchError> =>
        this.props.isTest ? mockSuccessResponse<string>('test') : parseResponse(await fetch(route)); 

    getFetchCountyAddressResponse = async (route: string): Promise<FetchSuccess<Address> | FetchError> =>
        this.props.isTest ? mockSuccessResponse<Address>({street: '1', city: 'city', state: 'NC', zipCode: 123}) : parseResponse(await fetch(route)); 
    
    getCountyPhoneNumber = async () => {
        try {
            const response = await this.getFetchCountyPhoneResponse('api/configuration/CountyPhoneNumber');
            if (response.ok) {
                const phoneNum = await response.getValue?.();
                this.setState({
                    countyPhoneNumber: phoneNum ?? 'N/A',
                    
                });
                return;
            }
        } 
        catch(error) {
            console.log(error);
            return false;
        }
     
    }
    getCountyAddress = async () => {
        try {
            const response = await this.getFetchCountyAddressResponse('api/configuration/CountyAddress');
            if (response.ok) {
                const address = await response.getValue?.();
                this.setState({
                    countyAddress: formatAddress(address)
                });
                return;
            }
        }
        catch (error) {
            console.log(error);
            return false;
        }

    }

    setBadUrlErrorMessage = () => this.setState({
        error: `Error getting payment service URL. Please contact the tax office at ${this.state.countyPhoneNumber}`
    })

    applyUrlTemplate = (urlTemplate: string, billInfo: GRBillState) => urlTemplate
        .replaceAll("{{BillNumber}}", billInfo.billNumber)
        .replaceAll("{{TaxYear}}", billInfo.taxYear)
        .replaceAll("{{accountNumber}}", this.props.location.state.accountNum.toString())
        .replaceAll("{{PIN}}", this.props.location.state.pin);

    redirectToVendorPayment = async () => {
        const billInfo = this.props.location.state.bill;
        if (billInfo && billInfo.billNumber && billInfo.taxYear) {
            try {
                const paymentUrlResponse = await this.fetchPaymentURL();                
                if (paymentUrlResponse.ok) {
                    const urlTemplate = await paymentUrlResponse.text();
                    if (urlTemplate) {
                        const cont = window.confirm('This will open a new tab. Payments may take some time to show in application. Would you like to continue?');
                        if (cont) {
                            const url = this.applyUrlTemplate(urlTemplate, billInfo);
                            window.open(url);
                        }
                    }
                    else {
                        this.setBadUrlErrorMessage();
                    }
                }
                else {
                    this.setBadUrlErrorMessage();
                }
            }
            catch {
                this.setState({
                    error: `Error getting URL. Call Tax Office at ${this.state.countyPhoneNumber}.`
                });
            }
        }
        else {
            this.setState({
                error: 'Error getting bill number and tax year for payment.'
            });
        }
    }

    get error() {
        return this.state.error ?
            (
                <Alert color="danger">
                    {this.state.error}
                </Alert>
            ) :
            (<></>);
    };

    get info() {
        return this.props.location.state.creditApplied > 0 ?
            (
                <Alert className="centered-container" color="warning">
                    Please print this page for your records. Direct payment is not currently allowed for bills with a credit applied amount. <br/>
                    Contact the tax office at {this.state.countyPhoneNumber} and/or mail in payment at {this.state.countyAddress}
                </Alert>
            ) :
            (
                <Alert className="centered-container" color="success">
                    Please print this page for your records. For any questions, please contact the tax office at {this.state.countyPhoneNumber}.
                </Alert>
            );
    };

    get printableComponent() {
        const paymentPossible = !this.props.location.state.currentDue || this.props.location.state.currentDue === 0 || this.props.location.state.creditApplied > 0;
        return (
            <>
                {this.error}
                {this.info}
                <div className="PrintSection" ref={(el: HTMLDivElement | null) => (this.componentRef = el)}>
                <PrintableReturnInfo {...this.props.location.state} />
                </div>
                    <div className="print-button-container">
                    <ReactToPrint
                        onBeforePrint={() => this.setState({ printing: true })}
                        onAfterPrint={() => this.setState({ printing: false })}
                        trigger={() => {
                            // Do not pass an `onClick` prop to the root node of the returned component as it will be overwritten.
                            return <LoadingButtonBase label="Print" className="print-button btn-left" loading={this.state.printing} />;
                        }}
                        content={() => this.componentRef}
                    />
                    <Button color="success" label="" id="btnPayment" className="payment-button btn-success btn-right" onClick={this.redirectToVendorPayment} loading={"false"} disabled={paymentPossible}>Pay Bill Now</Button>
                </div>
            </>);
    }
}
